import React, { useCallback, useMemo } from "react";
import { useBlockLayout, useRowSelect, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import { BaseButton } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import columns from "../columns/customLinkExposeManageColumns";
import { CustomLinkListParams, CustomLinkModel } from "src/api/customLink/custom-link-types";
import { useApiOperation } from "src/api/hooks";
import { patchCustomLinkAsync } from "src/api/customLink/custom-link";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";

type Props = {
  customLinkList: CustomLinkModel[];
  fetchGetCustomLinkList: () => void;
  handleCloseModal: () => void;
};

const CustomLinkExposeManageModal = ({ customLinkList, fetchGetCustomLinkList, handleCloseModal }: Props) => {
  const { executeAsync: patchCustomLink } = useApiOperation(patchCustomLinkAsync, { noLoading: true });
  const { setBaseModal } = useModal();
  const { openToast } = useToast();
  const handleChangeExposed = useCallback(
    async (id: string, value: boolean) => {
      const exposedItems = customLinkList.filter((item) => item.exposed);
      const isCurrentlyExposed = exposedItems.some((item) => item.id === id);

      // 토글을 켜는 경우(value === true)이고, 이미 3개 이상 노출되어 있고, 클릭한 항목이 현재 노출된 항목이 아닐 때만 모달 띄움
      if (value === true && exposedItems.length >= 5 && !isCurrentlyExposed) {
        openToast({
          content: "외부 링크는 최대 5개까지만 연결할 수 있습니다.",
          isSystemToast: true,
        });
        return;
      }
      await patchCustomLink({ id, exposed: value });
      fetchGetCustomLinkList();
    },
    [customLinkList, setBaseModal, patchCustomLink, fetchGetCustomLinkList],
  );
  const handleChangeOrder = useCallback(
    async (id1: string, id2: string, priority1: number, priority2: number) => {
      // 두 항목의 priority를 swap. 첫 번째가 실패하면 두 번째는 보내지 않는다
      await patchCustomLink({ id: id1, priority: priority2 });
      await patchCustomLink({ id: id2, priority: priority1 });
      fetchGetCustomLinkList();
    },
    [patchCustomLink, fetchGetCustomLinkList],
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns: columns as any,
      data: customLinkList,
      handleChangeExposed,
      handleChangeOrder,
    },
    useBlockLayout,
    useSticky,
    useRowSelect,
  );

  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  const maxDisplayOrderNumber = useMemo(() => {
    const exposedRows = rows.filter((row: any) => row.original.exposed);
    if (exposedRows.length === 0) return 0;
    return Math.max(...exposedRows.map((row: any) => row.values.displayOrderNumber || 0));
  }, [rows]);

  return (
    <BaseAbstractModal size="large" isOpen={true}>
      <section className="base-abstract-modal__title has-description">
        <h1>외부 링크 노출 관리</h1>
        <p>노출 미선택 시, Taap에 노출되지 않습니다.</p>
      </section>
      <section className="base-abstract-modal__contents px30 custom-link-example-02-wrapper">
        <div className="custom-link-example-02"></div>
      </section>
      <section className="base-abstract-modal__contents">
        <div {...getTableProps()} className="base-table view-data-table sticky px30">
          <div className="header">
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                {headerGroup.headers.map((header) => {
                  return (
                    <div
                      {...header.getHeaderProps()} //
                      className={`base-table__th `}
                    >
                      {header.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div {...getTableBodyProps()} className="body">
            {rows.map((row: any) => {
              prepareRow(row);
              const isLastExposedRow =
                row.original.exposed && row.values.displayOrderNumber === maxDisplayOrderNumber;
              return (
                <div
                  {...row.getRowProps()}
                  className={`base-table__tr ${row.values.floor ? "bg-gray100" : ""}`}
                >
                  {row.cells.map((cell: any) => {
                    return (
                      <div {...cell.getCellProps()} className={`base-table__td ${isLastExposedRow ? "last-exposed-row" : ""}`}>
                        {cell.render("Cell")}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div>
              {rows.length === 0 && (
                <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                  <div className="base-table__td w-100 text-center">
                    <div className="w-100">{<span>데이터가 없습니다.</span>}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className="base-abstract-modal__btn-wrap">
        <BaseButton
          title={"닫기"}
          onClick={() => {
            handleCloseModal();
          }}
        />
      </section>
    </BaseAbstractModal>
  );
};

export default CustomLinkExposeManageModal;
